import axios, { AxiosInstance, AxiosError } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(
  (config) => {
    const platformToken = getAccessToken();
    const serviceToken = localStorage.getItem('serviceToken');
    
    // Special case: pilot-service-access needs platform token
    const isPilotServiceAccess = config.url?.includes('/service-auth/pilot-service-access');

    // Use service token for marketplace and service endpoints (except pilot-service-access and admin endpoints)
    const isAdminEndpoint = config.url?.includes('/admin');
    const isClubShuttleAdmin = config.url?.includes('/clubs/') && config.url?.includes('/shuttles');
    const isGuestUsersAdmin = config.url?.includes('/service-auth/guest-users');
    const isServiceEndpoint = !isPilotServiceAccess && !isAdminEndpoint && !isClubShuttleAdmin && !isGuestUsersAdmin && (
      config.url?.includes('/marketplace') ||
      config.url?.includes('/service-auth') ||
      config.url?.includes('/shuttle') ||
      config.url?.includes('/equipment') ||
      config.url?.includes('/reminders')
    );
    
    const token = isServiceEndpoint && serviceToken ? serviceToken : (platformToken || serviceToken);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/token/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          setTokens(access_token, refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

export default api;